import React, { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { EmplContext } from "../Login_Page/Login_Page/LoginPage";
import "./HrWorkspaceLayout.css";
import "../../employee-modern-pages.css";

const primaryNav = [
  { label: "Overview", icon: "fa-grid-2", path: "/" },
  { label: "Employees", icon: "fa-users", path: "/hr/employees" },
  { label: "Leave approvals", icon: "fa-calendar-check", path: "/AllLeaveRequest" },
  { label: "Attendance", icon: "fa-fingerprint", path: "/myattendances" },
];

const workspaceNav = [
  { label: "Timesheets", icon: "fa-clock", path: "/mytimesheet" },
  { label: "Assets", icon: "fa-laptop", path: "/myasset" },
  { label: "Expenses", icon: "fa-receipt", path: "/myexpenses" },
  { label: "Policies", icon: "fa-book-open", path: "/companypolicies" },
];

const employeePrimaryNav = [
  { label: "Overview", icon: "fa-grid-2", path: "/" },
  { label: "Attendance", icon: "fa-fingerprint", path: "/myattendances" },
  { label: "Leave & Holidays", icon: "fa-calendar-check", path: "/holidays" },
  { label: "My Profile", icon: "fa-user", path: "/myprofile" },
];

const employeeWorkspaceNav = [
  { label: "Timesheets", icon: "fa-clock", path: "/mytimesheet" },
  { label: "Assets", icon: "fa-laptop", path: "/myasset" },
  { label: "Expenses", icon: "fa-receipt", path: "/myexpenses" },
  { label: "Learning", icon: "fa-book-open", path: "/mylearning" },
  { label: "Policies", icon: "fa-book-open-reader", path: "/companypolicies" },
  { label: "Gallery", icon: "fa-image", path: "/gallery" },
];

export default function HrWorkspaceLayout({ setIsHr, setIsmanager, mode = "hr" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { resultData, isemphr, isempmanager } = useContext(EmplContext);
  const employeeMode = mode === "employee";
  const navPrimary = employeeMode ? employeePrimaryNav : primaryNav;
  const navWorkspace = employeeMode ? employeeWorkspaceNav : workspaceNav;
  const displayName = resultData?.name || (employeeMode ? "Employee" : "HR Admin");
  // Every authenticated user is an employee; HR and Manager are added only when granted.
  const availableRoles = [
    { label: "HR", key: "HR", enabled: Boolean(isemphr) },
    { label: "Employee", key: "Employee", enabled: true },
    { label: "Manager", key: "Manager", enabled: Boolean(isempmanager) },
  ].filter((role) => role.enabled);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 960);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    let mobile = window.innerWidth <= 960;
    const syncSidebar = () => {
      const nextMobile = window.innerWidth <= 960;
      if (nextMobile !== mobile) {
        mobile = nextMobile;
        setSidebarOpen(!nextMobile);
      }
    };
    window.addEventListener("resize", syncSidebar);
    return () => window.removeEventListener("resize", syncSidebar);
  }, []);

  const isActive = (path) => location.pathname === path || (path !== "/" && location.pathname.startsWith(path));
  const pageMeta = {
    "/myattendances": { title: "Attendance", eyebrow: "Daily records", description: "Review your attendance history, working hours, and daily status.", icon: "fa-fingerprint" },
    "/holidays": { title: "Leave & Holidays", eyebrow: "Time off", description: "Track your leave balance, requests, and regional holidays.", icon: "fa-calendar-check" },
    "/myprofile": { title: "My Profile", eyebrow: "Personal workspace", description: "Keep your employee profile and workplace information up to date.", icon: "fa-user" },
    "/mytimesheet": { title: "Timesheets", eyebrow: "Work logs", description: "Review and manage your submitted timesheets and approvals.", icon: "fa-clock" },
    "/myasset": { title: "Assets", eyebrow: "Company resources", description: "View the equipment and assets assigned to you.", icon: "fa-laptop" },
    "/myexpenses": { title: "Expenses", eyebrow: "Reimbursements", description: "Submit and track your workplace expense requests.", icon: "fa-receipt" },
    "/mylearning": { title: "Learning", eyebrow: "Growth hub", description: "Explore learning resources and professional development content.", icon: "fa-book-open" },
    "/companypolicies": { title: "Policies", eyebrow: "Company knowledge", description: "Access the latest policies, guidelines, and workplace documents.", icon: "fa-book-open-reader" },
    "/gallery": { title: "Gallery", eyebrow: "Company moments", description: "Browse announcements, events, and shared workplace memories.", icon: "fa-image" },
    "/documents": { title: "Documents", eyebrow: "My workspace", description: "Find and manage the documents shared with you.", icon: "fa-file-lines" },
  };
  const activePageMeta = employeeMode ? pageMeta[location.pathname] : null;
  const go = (path) => {
    navigate(path);
    if (window.innerWidth < 960) setSidebarOpen(false);
  };

  const handleLogout = () => {
    window.localStorage.setItem("authenticated", JSON.stringify(false));
    window.localStorage.removeItem("authenticated");
    window.localStorage.removeItem("username");
    window.localStorage.removeItem("pin");
    window.localStorage.removeItem("isHr");
    window.localStorage.removeItem("isManager");
    window.sessionStorage.clear();
    setProfileOpen(false);
    window.location.href = "/login";
  };

  const openRequestWizard = (type) => {
    const routes = { leave: "/holidays", assets: "/myasset", expense: "/myexpenses", exception: "/myattendances" };
    go(routes[type] || "/");
  };

  const switchRole = (role) => {
    const nextIsHr = role === "HR";
    const nextIsManager = role === "Manager";
    setIsHr?.(nextIsHr);
    setIsmanager?.(nextIsManager);
    window.localStorage.setItem("isHr", String(nextIsHr));
    window.localStorage.setItem("isManager", String(nextIsManager));
    if (nextIsManager) navigate("/");
    else if (!nextIsHr) navigate("/");
  };

  return (
    <div className={`hr-modern-shell ${sidebarOpen ? "is-sidebar-open" : "is-sidebar-closed"}`}>
      <button className="hr-shell-scrim" type="button" aria-label="Close navigation" onClick={() => setSidebarOpen(false)} />

      <aside id="hr-workspace-sidebar" className="hr-modern-sidebar" aria-label="HR workspace navigation">
        <div className="hr-modern-brand">
          <img className="hr-modern-brand-logo" src={`${process.env.PUBLIC_URL}/company_logo.png`} alt="O2B Technologies" />
        </div>
        <nav className="hr-modern-nav">
          {navPrimary.map((item) => <button key={item.path} type="button" className={isActive(item.path) ? "active" : ""} onClick={() => go(item.path)}><i className={`fa-solid ${item.icon}`} /><span>{item.label}</span>{item.label === "Leave approvals" && <b>8</b>}</button>)}
        </nav>
        <div className="hr-modern-nav-label">Manage</div>
        <nav className="hr-modern-nav">
          {navWorkspace.map((item) => <button key={item.path} type="button" className={isActive(item.path) ? "active" : ""} onClick={() => go(item.path)}><i className={`fa-solid ${item.icon}`} /><span>{item.label}</span></button>)}
        </nav>
      </aside>

      <div className="hr-modern-main">
        <header className="hr-modern-topbar">
          <div className="hr-topbar-left">
            <button className="hr-menu-button" type="button" onClick={() => setSidebarOpen((open) => !open)} aria-controls="hr-workspace-sidebar" aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"} aria-expanded={sidebarOpen}><i className={`fa-solid ${sidebarOpen ? "fa-xmark" : "fa-bars"}`} /></button>
            {!sidebarOpen && <div className="hr-topbar-brand"><img className="hr-topbar-brand-logo" src={`${process.env.PUBLIC_URL}/company_logo.png`} alt="O2B Technologies" /></div>}
            <div className="hr-breadcrumb" aria-hidden="true" />
          </div>
          <div className="hr-topbar-actions">
            <div className="hr-request-menu"><button className="hr-request-button" type="button"><i className="fa-solid fa-plus" /> Raise a request <i className="fa-solid fa-chevron-down" /></button><div className="hr-request-popover"><button type="button" onClick={() => openRequestWizard("leave")}><i className="fa-solid fa-calendar-days" /> Apply a Leave</button><button type="button" onClick={() => openRequestWizard("assets")}><i className="fa-solid fa-laptop" /> Request for Assets</button><button type="button" onClick={() => openRequestWizard("expense")}><i className="fa-solid fa-receipt" /> Request for Expense</button><button type="button" onClick={() => openRequestWizard("exception")}><i className="fa-solid fa-bell" /> Raise Exception</button></div></div>
            {availableRoles.length > 1 && <div className={`hr-role-switch role-count-${availableRoles.length}`} aria-label="Switch portal role">{availableRoles.map((role) => <button key={role.key} type="button" className={(role.key === "HR" && !employeeMode) || (role.key === "Employee" && employeeMode) ? "active" : ""} onClick={() => switchRole(role.key)}>{role.label}</button>)}</div>}
            <div className={`hr-profile-menu ${profileOpen ? "is-open" : ""}`}>
              <button className="hr-user-chip" type="button" onClick={() => setProfileOpen((open) => !open)} aria-expanded={profileOpen} aria-haspopup="menu"><span className="hr-avatar hr-avatar-small">{displayName.split(" ").map((part) => part[0]).slice(0, 2).join("").toUpperCase()}</span><span className="hr-user-name">{displayName.split(" ")[0]}</span><i className={`fa-solid ${profileOpen ? "fa-chevron-up" : "fa-chevron-down"}`} /></button>
              {profileOpen && <div className="hr-profile-popover" role="menu"><button type="button" onClick={() => navigate("/myprofile")}><i className="fa-solid fa-user" /> My profile</button><button type="button" onClick={handleLogout}><i className="fa-solid fa-right-from-bracket" /> Logout</button></div>}
            </div>
          </div>
        </header>
            <div className={`hr-modern-content ${employeeMode ? "employee-modern-content" : ""} ${activePageMeta ? `employee-page-${activePageMeta.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : "employee-page-overview"}`}>
              {activePageMeta && <section className="employee-page-header" aria-labelledby="employee-page-title">
                <div className="employee-page-heading-copy">
                  <span className="employee-page-eyebrow"><i className={`fa-solid ${activePageMeta.icon}`} /> {activePageMeta.eyebrow}</span>
                  <h1 id="employee-page-title">{activePageMeta.title}</h1>
                  <p>{activePageMeta.description}</p>
                </div>
                <div className="employee-page-header-status"><span className="employee-live-dot" /> Employee workspace</div>
              </section>}
              <Outlet />
            </div>
      </div>

    </div>
  );
}
